import { memo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH } from '@fortawesome/pro-solid-svg-icons/faEllipsisH';
import { faPen } from '@fortawesome/pro-regular-svg-icons/faPen';
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash';

import { useIntl } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import { FormattedCurrency } from '../../../../../../../components/formatted-currency';
import { SupplierNameCell } from '../supplier-name-cell/supplier-name-cell';
import type { ProductSupplier } from '../../product-suppliers-types';
import { messages } from '../../product-suppliers-tab.messages';
import style from '../../product-suppliers-tab.module.less';

type SupplierRowProps = {
  supplier: ProductSupplier;
  onEdit: (supplier: ProductSupplier) => void;
  onDelete: (supplier: ProductSupplier) => void;
};

export const SupplierRow = memo(({ supplier, onEdit, onDelete }: SupplierRowProps) => {
  const intl = useIntl();
  const handleEdit = useCallback(() => onEdit(supplier), [onEdit, supplier]);
  const handleDelete = useCallback(() => onDelete(supplier), [onDelete, supplier]);

  return (
    <div className={style['suppliers-row']}>
      <div className={style['cell-link']}>
        <SupplierNameCell supplierId={supplier.supplierId} supplierName={supplier.supplierName} />
      </div>
      <Text className={style['cell-readonly']} element='div' textType={TextType.Caption} value={supplier.uomSku ?? ''} />
      <Text className={style['cell-readonly']} element='div' textType={TextType.Caption} value={supplier.supplierSku} />
      <div className={style['cell-readonly']}>
        <FormattedCurrency value={supplier.cost} />
      </div>
      <FlexRow className={style['cell-actions']}>
        <DropdownMenu menuPosition='bottom-right'>
          <DropdownMenuToggler
            ariaLabel={intl.formatMessage(messages.rowActionsMenu)}
            className={style['menu-toggle']}
            hideChevron
            openClassName={style['menu-toggle-open']}
            theme='link'
          >
            <FlexRow className={style['menu-toggle-contents']}>
              <FontAwesomeIcon icon={faEllipsisH} />
            </FlexRow>
          </DropdownMenuToggler>
          <DropdownMenuItem onClick={handleEdit}>
            <FlexRow className={style['menu-item-row']}>
              <FontAwesomeIcon icon={faPen} />
              <Text textType={TextType.Paragraph} value={messages.rowEdit} />
            </FlexRow>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleDelete}>
            <FlexRow className={style['menu-item-row-delete']}>
              <FontAwesomeIcon icon={faTrash} />
              <Text textType={TextType.Paragraph} value={messages.rowDelete} />
            </FlexRow>
          </DropdownMenuItem>
        </DropdownMenu>
      </FlexRow>
    </div>
  );
});
